import { createAsyncThunk } from "@reduxjs/toolkit";

const BASE_URL = `${process.env.REACT_APP_API_URL}/incidents`;

export const fetchIncidentDashboard = createAsyncThunk('incident/fetchDashboard', async (range, { rejectWithValue, signal }) => {
  try {
    const response = await fetch(`${BASE_URL}/dashboard?${new URLSearchParams(range)}`, {
      signal, headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.message || 'Unable to load incident dashboard');
    if (!Array.isArray(result.data?.records) || result.data.range?.startDate !== range.startDate || result.data.range?.endDate !== range.endDate) throw new Error('Invalid incident dashboard response');
    return result.data;
  } catch (error) { return rejectWithValue(error.message || 'Unable to load incident dashboard'); }
});

export const submitIncident = createAsyncThunk(
  "incident/submitIncident",
  async (incident, { rejectWithValue }) => {
    try {
      const body = new FormData();

      // ==========================================
      // Common fields
      // ==========================================

      if(incident.incidentType) {
        body.append("type", incident.incidentType);
      }

      if(incident.location) {
        body.append("location", incident.location);
      }

      if(incident.date && incident.time) {
        body.append("incidentDateTime", `${incident.date}T${incident.time}:00`);
      }

      if (incident.description) {
        body.append("description", incident.description);
      }

      if (incident.immediateAction) {
        body.append("immediateAction", incident.immediateAction);
      }

      // ==========================================
      // Customer Injury
      // ==========================================

      if (incident.injuredPerson) {
        body.append("injuredPerson", incident.injuredPerson);
      }

      if (incident.injuryType) {
        body.append("injuryType", incident.injuryType);
      }

      // ==========================================
      // Staff Injury
      // ==========================================

      if (incident.staffMemberId) {
        body.append("staffMemberId", incident.staffMemberId);
      }

      if (incident.bodyPart) {
        body.append("bodyPart", incident.bodyPart);
      }

      // ==========================================
      // Security Incident
      // ==========================================

      if (incident.securityType) {
        body.append("securityType", incident.securityType);
      }

      // ==========================================
      // Food Spill
      // ==========================================

      if (incident.foodSpillType) {
        body.append("foodSpillType", incident.foodSpillType);
      }

      if (incident.foodItemInvolved) {
        body.append("foodItemInvolved", incident.foodItemInvolved);
      }

      // ==========================================
      // Allergen / Dietary
      // ==========================================

      if (incident.allergen) {
        body.append("allergen", incident.allergen);
      }

      // ==========================================
      // Equipment
      // ==========================================

      if (incident.equipment) {
        body.append("equipment", incident.equipment);
      }

      if (incident.impact) {
        body.append("impact", incident.impact);
      }

      // ==========================================
      // Property Damage
      // ==========================================

      if (incident.damageType) {
        body.append("damageType", incident.damageType);
      }

      if (
        incident.estimatedCost !== undefined &&
        incident.estimatedCost !== null &&
        incident.estimatedCost !== ""
      ) {
        body.append("estimatedCost", incident.estimatedCost);
      }

      // ==========================================
      // Customer Complaint
      // ==========================================

      if (incident.complaintType) {
        body.append("complaintType", incident.complaintType);
      }

      // ==========================================
      // Fire Safety
      // ==========================================

      if (incident.fireSafetyType) {
        body.append("fireSafetyType", incident.fireSafetyType);
      }

      // ==========================================
      // Other Incident
      // ==========================================

      if (incident.otherIncidentType) {
        body.append("otherIncidentType", incident.otherIncidentType);
      }

      // ==========================================
      // STATUS
      // ==========================================

      if (incident.status) {
        body.append("status", incident.status);
      }

      // ==========================================
      // REFERRED TO
      // ==========================================

      if (
        incident.referredToId !== undefined &&
        incident.referredToId !== null &&
        incident.referredToId !== ""
      ) {
        body.append("referredToId", incident.referredToId);
      } else if (incident.referredToId === null) {
        body.append("referredToId", "");
      }

      // ==========================================
      // CORRECTIVE ACTION
      // ==========================================

      if (
        incident.correctiveAction !== undefined &&
        incident.correctiveAction !== null
      ) {
        body.append("correctiveAction", incident.correctiveAction);
      }

      // ==========================================
      // ROOT CAUSE
      // ==========================================

      if (incident.rootCause !== undefined && incident.rootCause !== null) {
        body.append("rootCause", incident.rootCause);
      }

      // ==========================================
      // OPTIONAL UPDATE ID
      // ==========================================

      if (
        incident.id !== undefined &&
        incident.id !== null &&
        incident.id !== ""
      ) {
        body.append("id", incident.id);
      }

      // ==========================================
      // PHOTOS
      // ==========================================

      if (incident.photos?.length) {
        incident.photos.forEach((file) => {
          body.append("photos", file);
        });
      }

      // ==========================================
      // API REQUEST
      // ==========================================

      const token = localStorage.getItem("token");

      const response = await fetch(`${BASE_URL}/submit`, {
        method: "POST",

        body,

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to submit incident",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error occurred while submitting incident",
      );
    }
  },
);

export const getIncidents = createAsyncThunk(
  "incident/getIncidents",
  async (_, { rejectWithValue }) => {
    try {
      
      const token = localStorage.getItem("token");

      const response = await fetch(BASE_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to fetch incidents",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.message || "Network error occurred while fetching incidents",
      );
    }
  },
);
